import { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowRight, MapPin, ExternalLink } from "lucide-react";
import Layout from "@/components/Layout";
import SEO from "@/components/SEO";
import VerificationLabel from "@/components/VerificationLabel";
import { api } from "@/lib/api";

/**
 * Phase B — Florida Transfer Map.
 * Inline SVG state outline (CC0 / public-domain simplified path from US Census TIGER,
 * hand-fitted to a 600x520 viewbox for our purposes). NO external tile providers.
 * Attribution: US Census TIGER/Line shapefiles (public domain).
 */

// Simplified Florida outline — coarse but recognizable. Coordinates fit the FL bounding box
// (lat 24.5→31.0 mapped to y 500→30; lng -87.6→-79.9 mapped to x 20→580).
const FL_PATH = "M 45,55 L 480,55 L 495,90 L 505,140 L 505,180 L 480,215 L 455,230 L 420,240 L 405,270 L 400,320 L 405,360 L 425,395 L 445,415 L 445,440 L 430,470 L 400,485 L 365,490 L 335,485 L 305,470 L 285,440 L 265,410 L 250,375 L 240,340 L 235,305 L 235,270 L 240,240 L 220,225 L 195,215 L 165,205 L 130,195 L 100,180 L 75,155 L 55,120 L 45,90 Z";

// FL bounding box (approximate, generous)
const BBOX = { minLat: 24.5, maxLat: 31.0, minLng: -87.6, maxLng: -79.9 };
const VB = { w: 600, h: 520, padX: 40, padY: 40 };

function project(lat, lng) {
  const usableW = VB.w - 2 * VB.padX;
  const usableH = VB.h - 2 * VB.padY;
  const x = VB.padX + ((lng - BBOX.minLng) / (BBOX.maxLng - BBOX.minLng)) * usableW;
  const y = VB.padY + ((BBOX.maxLat - lat) / (BBOX.maxLat - BBOX.minLat)) * usableH;
  return { x, y };
}

const CHIPS = [
  { key: "community_college", label: "Community Colleges" },
  { key: "state_university",  label: "Universities" },
  { key: "out_of_state",      label: "Selective destinations" },
];

export default function TransferMap() {
  const [flInstitutions, setFlInstitutions] = useState([]);
  const [outOfState, setOutOfState] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [selected, setSelected] = useState(null);
  const [roadmaps, setRoadmaps] = useState([]);
  const [visibleGroups, setVisibleGroups] = useState({ community_college: true, state_university: true, out_of_state: true });
  const [hoverSlug, setHoverSlug] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true); setError(false);
    try {
      const [flRes, allRes] = await Promise.all([
        api.get("/institutions", { params: { state: "FL", page_size: 100 } }),
        api.get("/institutions", { params: { page_size: 200 } }),
      ]);
      const fl = (flRes.data.items || []).filter((i) => typeof i.lat === "number" && typeof i.lng === "number");
      setFlInstitutions(fl);
      const outSide = (allRes.data.items || []).filter((i) => (i.location?.state || "") !== "FL");
      setOutOfState(outSide);
    } catch (e) { setError(true); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { fetchAll(); }, [fetchAll]);

  const selectNode = useCallback(async (inst) => {
    setSelected(inst);
    setRoadmaps([]);
    if (!inst) return;
    try {
      const isCC = inst.type === "community_college";
      const params = isCC ? { source_slug: inst.slug } : { destination_slug: inst.slug };
      const res = await api.get("/roadmaps", { params });
      setRoadmaps(res.data.items || []);
    } catch (_e) { /* silent — panel still renders */ }
  }, []);

  // Esc to close panel
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") setSelected(null); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const toggleChip = (k) => setVisibleGroups((v) => ({ ...v, [k]: !v[k] }));

  const ccs = useMemo(() => flInstitutions.filter((i) => i.type === "community_college"), [flInstitutions]);
  const unis = useMemo(() => flInstitutions.filter((i) => i.type !== "community_college"), [flInstitutions]);
  const hasNoData = !loading && flInstitutions.length === 0;

  return (
    <Layout>
      <SEO
        title="Florida Transfer Map — Community College to University Pathways | Beyond Valencia"
        description="Interactive map of Florida's community college → state university transfer network. Drill into any campus for verified transfer roadmaps."
        canonical="/transfer-map"
      />

      <section className="bg-bone border-b border-stone/30" data-testid="transfer-map-page">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-12 lg:py-16">
          <div className="text-[11px] uppercase tracking-[0.24em] text-brass font-medium">The Transfer System</div>
          <h1 className="mt-5 font-serif font-medium text-4xl sm:text-5xl lg:text-6xl text-ivy tracking-tight leading-[1.05]" data-testid="transfer-map-headline">
            Florida&rsquo;s Transfer Map
          </h1>
          <p className="mt-6 max-w-3xl text-base sm:text-lg text-ink-soft leading-relaxed">
            Every community college and university in the Florida transfer network — plus the selective destinations beyond it. Click any node to see the roadmaps we&rsquo;ve verified for that campus.
          </p>

          {/* Filter chips */}
          <div className="mt-8 flex flex-wrap gap-2" data-testid="map-filter-chips">
            {CHIPS.map((c) => (
              <button
                key={c.key}
                onClick={() => toggleChip(c.key)}
                className={`text-[11px] uppercase tracking-[0.18em] font-semibold px-3 py-1.5 border transition-colors ${visibleGroups[c.key] ? "bg-ivy text-bone border-ivy" : "bg-bone text-ink border-stone/40 hover:border-ivy"}`}
                data-testid={`map-chip-${c.key}`}
                aria-pressed={visibleGroups[c.key]}
              >
                {c.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-bone-dim/30">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-10 lg:py-14">
          <div className={`grid ${selected ? "lg:grid-cols-[1fr_380px]" : "lg:grid-cols-1"} gap-8`}>

            {/* MAP CANVAS */}
            <div className="border border-stone/40 bg-bone p-4 sm:p-6" data-testid="transfer-map-canvas">
              {hasNoData ? (
                <EmptyState onRetry={fetchAll} />
              ) : error ? (
                <ErrorState onRetry={fetchAll} />
              ) : loading ? (
                <div className="py-24 text-center text-stone text-sm">Loading map…</div>
              ) : (
                <svg
                  viewBox={`0 0 ${VB.w} ${VB.h}`}
                  className="w-full h-auto max-h-[70vh]"
                  role="img"
                  aria-label="Florida transfer map"
                  data-testid="transfer-map-svg"
                >
                  {/* State outline */}
                  <path d={FL_PATH} fill="#EFE7D3" stroke="#0F392B" strokeWidth="1.5" opacity="0.85" />

                  {/* CC nodes */}
                  {visibleGroups.community_college && ccs.map((i) => {
                    const { x, y } = project(i.lat, i.lng);
                    const isHover = hoverSlug === i.slug;
                    return (
                      <g key={i.slug} className="cursor-pointer" onClick={() => selectNode(i)}
                         onMouseEnter={() => setHoverSlug(i.slug)} onMouseLeave={() => setHoverSlug(null)}
                         onFocus={() => setHoverSlug(i.slug)} onBlur={() => setHoverSlug(null)}
                         tabIndex={0} role="button" aria-label={i.name}
                         onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); selectNode(i); } }}
                         data-testid={`map-node-${i.slug}`}>
                        <circle cx={x} cy={y} r={isHover ? 11 : 8} fill="#A88A4A" stroke="#0F392B" strokeWidth="1.5" />
                        {isHover && (
                          <g>
                            <rect x={x + 14} y={y - 16} width={i.name.length * 6.6 + 14} height={26} fill="#0F392B" />
                            <text x={x + 21} y={y + 2} fontSize="12" fill="#F5F1E8" fontFamily="Inter, sans-serif">{i.name}</text>
                          </g>
                        )}
                      </g>
                    );
                  })}

                  {/* University nodes */}
                  {visibleGroups.state_university && unis.map((i) => {
                    const { x, y } = project(i.lat, i.lng);
                    const isHover = hoverSlug === i.slug;
                    return (
                      <g key={i.slug} className="cursor-pointer" onClick={() => selectNode(i)}
                         onMouseEnter={() => setHoverSlug(i.slug)} onMouseLeave={() => setHoverSlug(null)}
                         onFocus={() => setHoverSlug(i.slug)} onBlur={() => setHoverSlug(null)}
                         tabIndex={0} role="button" aria-label={i.name}
                         onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); selectNode(i); } }}
                         data-testid={`map-node-${i.slug}`}>
                        <circle cx={x} cy={y} r={isHover ? 14 : 11} fill="#0F392B" stroke="#A88A4A" strokeWidth="2.5" />
                        <text x={x} y={y - 16} textAnchor="middle" fontSize="12" fontWeight="600" fill="#0F392B" fontFamily="EB Garamond, serif">
                          {i.name.replace("University of ", "").replace("Florida State University", "FSU")}
                        </text>
                      </g>
                    );
                  })}

                  {/* Legend */}
                  <g transform={`translate(${VB.w - 180}, ${VB.h - 70})`}>
                    <rect width="170" height="60" fill="#F5F1E8" stroke="#0F392B" strokeWidth="1" />
                    <circle cx="16" cy="20" r="6" fill="#A88A4A" stroke="#0F392B" strokeWidth="1.2" />
                    <text x="28" y="24" fontSize="10" fill="#0F392B" fontFamily="Inter, sans-serif">Community College</text>
                    <circle cx="16" cy="42" r="7" fill="#0F392B" stroke="#A88A4A" strokeWidth="1.8" />
                    <text x="28" y="46" fontSize="10" fill="#0F392B" fontFamily="Inter, sans-serif">State University</text>
                  </g>
                </svg>
              )}
              <p className="mt-4 text-[10px] uppercase tracking-[0.22em] text-stone">Map is illustrative · Coordinates from US Census TIGER (public domain).</p>
            </div>

            {/* SIDE PANEL — desktop right rail, mobile bottom sheet */}
            <AnimatePresence>
              {selected && (
                <>
                  {/* Backdrop for mobile bottom sheet */}
                  <div className="lg:hidden fixed inset-0 bg-ink/60 z-40" onClick={() => setSelected(null)} />
                  <motion.div
                    initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 20 }}
                    className="fixed lg:relative bottom-0 left-0 right-0 lg:inset-auto z-50 lg:z-auto max-h-[80vh] lg:max-h-none overflow-y-auto border-t lg:border lg:border-stone/40 border-stone/40 bg-bone p-6 lg:p-7"
                    data-testid="transfer-map-panel"
                    role="dialog"
                    aria-labelledby="tm-panel-title"
                  >
                    <MapPanel
                      inst={selected}
                      roadmaps={roadmaps}
                      onClose={() => setSelected(null)}
                    />
                  </motion.div>
                </>
              )}
            </AnimatePresence>
          </div>

          {/* Out-of-state strip */}
          {visibleGroups.out_of_state && outOfState.length > 0 && (
            <div className="mt-14" data-testid="out-of-state-strip">
              <h2 className="font-serif text-2xl sm:text-3xl text-ivy tracking-tight">Selective destinations beyond Florida</h2>
              <p className="mt-2 text-sm text-ink-soft max-w-2xl">Winnable from a Florida community college with a coherent academic thesis and sustained excellence at the transfer institution.</p>
              <div className="mt-6 flex flex-wrap gap-4">
                {outOfState.map((i) => (
                  <button key={i.slug} onClick={() => selectNode(i)} className="border border-stone/40 bg-bone hover:border-ivy transition-colors p-5 text-left min-w-[240px]" data-testid={`out-of-state-card-${i.slug}`}>
                    <div className="text-[10px] uppercase tracking-[0.22em] text-brass font-semibold">Private University</div>
                    <div className="font-serif text-xl text-ivy mt-2 tracking-tight">{i.name}</div>
                    <div className="text-xs text-stone mt-1 uppercase tracking-[0.16em]">
                      <MapPin className="h-3 w-3 inline mr-1" />{i.location?.city}, {i.location?.state}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>
    </Layout>
  );
}

function MapPanel({ inst, roadmaps, onClose }) {
  const isCC = inst.type === "community_college";
  const basePath = isCC ? "/community-colleges" : "/universities";
  const excerpt = (inst.body_markdown || "").replace(/^#+ .*$/gm, "").replace(/\*\*/g, "").replace(/\n+/g, " ").trim().slice(0, 220);
  return (
    <>
      <div className="flex items-baseline justify-between gap-3 mb-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-brass font-semibold">{isCC ? "Community College" : (inst.type === "private_university" ? "Selective destination" : "State University")}</div>
          <h3 id="tm-panel-title" className="font-serif text-2xl text-ivy tracking-tight mt-1">{inst.name}</h3>
          {inst.location && <p className="text-xs text-stone mt-1 uppercase tracking-[0.16em]"><MapPin className="h-3 w-3 inline mr-1" />{inst.location.city}, {inst.location.state}</p>}
        </div>
        <button onClick={onClose} aria-label="Close" data-testid="map-panel-close" className="text-stone hover:text-ivy p-1"><X className="h-5 w-5" /></button>
      </div>
      <div className="flex items-center gap-2 mb-4">
        <VerificationLabel status={inst.verification_status || "awaiting"} />
      </div>
      {excerpt && <p className="text-sm text-ink-soft leading-relaxed">{excerpt}…</p>}

      <div className="mt-6">
        <div className="text-[10px] uppercase tracking-[0.22em] text-brass font-semibold mb-3">
          {isCC ? "Transfer roadmaps from here" : "Incoming roadmaps"}
        </div>
        {roadmaps.length === 0 ? (
          <p className="text-sm text-stone italic">No verified roadmaps yet for this campus. Ask NEO to draft one.</p>
        ) : (
          <ul className="space-y-2" data-testid="map-panel-roadmaps">
            {roadmaps.map((r) => {
              const other = isCC ? r.to_institution : r.from_institution;
              return (
                <li key={r.slug}>
                  <Link to={`/roadmaps/${r.slug}`} className="block border border-stone/40 hover:border-ivy p-3 transition-colors" data-testid={`map-panel-roadmap-${r.slug}`}>
                    <div className="text-[10px] uppercase tracking-[0.18em] text-brass font-semibold">{isCC ? "→ Destination" : "← From"}</div>
                    <div className="text-sm text-ivy mt-1 font-medium">{other?.name || r.title || r.slug}</div>
                    <div className="text-[11px] text-stone mt-1">{r.title}</div>
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <div className="mt-6 pt-5 border-t border-stone/30 flex flex-col gap-3">
        <Link to={`${basePath}/${inst.slug}`} className="inline-flex items-center justify-center gap-2 bg-brass hover:bg-brass-soft text-ink uppercase tracking-[0.18em] text-[11px] font-semibold py-2.5 px-4 transition-colors" data-testid="map-panel-profile-link">
          View full profile <ArrowRight className="h-3.5 w-3.5" />
        </Link>
        {inst.official_source_url && (
          <a href={inst.official_source_url} target="_blank" rel="noopener noreferrer" className="text-[11px] text-stone hover:text-ivy inline-flex items-center gap-1 justify-center">
            <ExternalLink className="h-3 w-3" /> Official transfer page
          </a>
        )}
      </div>
    </>
  );
}

function EmptyState({ onRetry }) {
  return (
    <div className="py-16 text-center px-6">
      <div className="text-[10px] uppercase tracking-[0.22em] text-brass font-semibold">Map data</div>
      <h3 className="mt-3 font-serif text-2xl text-ivy tracking-tight">Map is still being curated.</h3>
      <p className="mt-3 text-sm text-ink-soft">Explore the catalog directly while we finalize coordinates.</p>
      <div className="mt-5 flex flex-wrap gap-3 justify-center">
        <Link to="/community-colleges" className="border border-ivy text-ivy hover:bg-ivy hover:text-bone uppercase tracking-[0.18em] text-[11px] font-semibold py-2.5 px-4">Community colleges →</Link>
        <Link to="/universities" className="border border-ivy text-ivy hover:bg-ivy hover:text-bone uppercase tracking-[0.18em] text-[11px] font-semibold py-2.5 px-4">Universities →</Link>
      </div>
      <button onClick={onRetry} className="mt-6 text-[11px] uppercase tracking-[0.18em] text-stone hover:text-ivy">Retry</button>
    </div>
  );
}

function ErrorState({ onRetry }) {
  return (
    <div className="py-16 text-center">
      <h3 className="font-serif text-2xl text-ivy tracking-tight">Couldn&rsquo;t load the map.</h3>
      <button onClick={onRetry} className="mt-4 bg-brass hover:bg-brass-soft text-ink uppercase tracking-[0.18em] text-[11px] font-semibold py-2.5 px-5" data-testid="map-retry">Retry</button>
    </div>
  );
}
